import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import { Role } from '@/Enums/Users/Role.enum';
import { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IClientControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IReportingFilterModel } from '@/Interfaces/Filters/FilterModel.interface';
import {
  IBeeswaxReportingApiFilters,
  IGoogleReportingApiFilters,
} from '@/Interfaces/ReportingDashboard/ApiFilters.interface';
import { getWhitelabelOptions } from '@/helpers/filters/filterOptions';
import { apiTabPositionToEnum } from '@/helpers/reportingDashboard';
import { getClientsList, getUpdatedTabFilters } from '@/helpers/reportingDashboard/slice';
import { RootState } from '@/store';
import { selectVisibleReportingScreenTabs } from '@/store/selectors/currentWhitelabel.selectors';
import {
  selectReportingDashboardClients,
  selectReportingDashboardWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import { getAdvertiserCampaignFilters, getControlOptionsValues } from '../filters/filterMapping';

// The whitelabel/client FILTER OPTIONS (built from the raw, un-tab-scoped
// whitelabel catalog) carry every advertiser regardless of platform. The
// no-selection fallback already scopes its own whitelabel/client catalog via
// `getWhitelabelsList`/`getClientsList` (see shared.selectors.ts), but an
// explicit whitelabel/client selection bypassed that scoping entirely, so
// picking one whitelabel could submit MORE (cross-platform) advertiser ids
// than no selection did (flank #4686). Apply the same per-tab scoping to the
// user's own selection before extracting ids.
const getGoogleScopedWhitelabels = (
  whitelabels: IWhitelabelControlOption<IParsedAdvertiser>[] = [],
): IWhitelabelControlOption<IParsedAdvertiser>[] =>
  whitelabels.map(whitelabel => ({ ...whitelabel, clients: getClientsList(ApiTabIndex.Google, whitelabel.clients) }));

export const getBeeswaxReportingFilter = (
  filters: IReportingFilterModel<IParsedAdvertiser>,
  role: Role,
  clients: IClientControlOption<IParsedAdvertiser>[],
  whitelabels: Pick<IWhitelabelControlOption<IParsedAdvertiser>, 'clients'>[],
): IBeeswaxReportingApiFilters => {
  const { advertiserIds, campaignIds } = getAdvertiserCampaignFilters<IParsedAdvertiser, 'ribeyeAdvertiserId'>(
    filters,
    role,
    clients,
    whitelabels,
    'ribeyeAdvertiserId',
  );

  return {
    dateRangeStart: filters.dateStart,
    dateRangeEnd: filters.dateEnd,
    advertiserIds,
    campaignIds,
    lineItemIds: getControlOptionsValues(filters.placement),
  };
};

// flank#4689: the API only accepts one whitelabelId, so a multi-select silently
// falls back to the viewer's own Whitelabel (see getGoogleReportingFilter below)
// instead of reporting on the actual selection. Surfaced explicitly rather than
// left to read as "no data."
export const GOOGLE_MULTI_WHITELABEL_MESSAGE = 'Google reporting covers one Whitelabel at a time — pick one.';

export const isGoogleMultiWhitelabelSelection = (
  filters: Pick<IReportingFilterModel<IParsedAdvertiser>, 'whitelabels'>,
) => getControlOptionsValues(filters.whitelabels).length > 1;

export const getGoogleReportingFilter = (
  filters: IReportingFilterModel<IParsedAdvertiser>,
  role: Role,
  clients: IClientControlOption<IParsedAdvertiser>[],
  whitelabels: IWhitelabelControlOption<IParsedAdvertiser>[],
): IGoogleReportingApiFilters => {
  const { advertiserIds, campaignIds } = getAdvertiserCampaignFilters<IParsedAdvertiser, 'ribeyeAdvertiserId'>(
    {
      ...filters,
      whitelabels: getGoogleScopedWhitelabels(filters.whitelabels),
      clients: getClientsList(ApiTabIndex.Google, filters.clients),
    },
    role,
    clients,
    whitelabels,
    'ribeyeAdvertiserId',
  );

  // Pass whitelabelIds so the backend resolves the DV360 partner from the
  // Whitelabel being reported on, not the viewing user's own — otherwise
  // staff checking a client's Google/YouTube reporting on their behalf get
  // an empty report (queried under the wrong partner). Mirrors the GAM
  // reporting filter's same pattern.
  //
  // filters.whitelabels (the SELECTION), not the `whitelabels` param (every
  // option for the tab, e.g. every WL for a super admin) — the API now
  // rejects more than one whitelabelId with a 400 (review, Lucas), so
  // passing the full option list broke the Google tab for any admin/org
  // admin as soon as that validation shipped.
  //
  // Only sent when exactly one Whitelabel is selected (review, Lucas): the
  // same 400 fires if the user picks more than one, since the API accepts a
  // single whitelabelId. Omitting it for a multi-select falls back to the
  // viewer's own Whitelabel — the pre-existing behavior — rather than
  // erroring out the whole tab.
  const selectedWhitelabelIds = getControlOptionsValues(filters.whitelabels);
  const whitelabelIds = selectedWhitelabelIds.length === 1 ? selectedWhitelabelIds : [];

  return {
    dateRangeStart: filters.dateStart,
    dateRangeEnd: filters.dateEnd,
    advertiserIds,
    campaignIds,
    ...(whitelabelIds.length ? { whitelabelIds } : {}),
  };
};

export const getAdswizzReportingFilter = (
  filters: IReportingFilterModel<IParsedAdvertiser>,
  role: Role,
  clients: IClientControlOption<IParsedAdvertiser>[],
  whitelabels: Pick<IWhitelabelControlOption<IParsedAdvertiser>, 'clients'>[],
): IBeeswaxReportingApiFilters => {
  const { advertiserIds, campaignIds } = getAdvertiserCampaignFilters<IParsedAdvertiser, 'ribeyeAdvertiserId'>(
    filters,
    role,
    clients,
    whitelabels,
    'ribeyeAdvertiserId',
  );

  return {
    dateRangeStart: filters.dateStart,
    dateRangeEnd: filters.dateEnd,
    advertiserIds,
    campaignIds,
  };
};

export const getReportingFilter = (
  filters: IReportingFilterModel<IParsedAdvertiser>,
  role: Role,
  clients: IClientControlOption<IParsedAdvertiser>[],
  whitelabels: Pick<IWhitelabelControlOption<IParsedAdvertiser>, 'clients'>[],
): IBeeswaxReportingApiFilters => {
  const { advertiserIds, campaignIds } = getAdvertiserCampaignFilters<IParsedAdvertiser, 'ribeyeAdvertiserId'>(
    filters,
    role,
    clients,
    whitelabels,
    'ribeyeAdvertiserId',
  );

  return {
    dateRangeStart: filters.dateStart,
    dateRangeEnd: filters.dateEnd,
    advertiserIds,
    campaignIds,
  };
};

export const getEffectiveFilters = (state: RootState, foreignApiTab: ApiTabIndex) => {
  const {
    reportingDashboard: { filters: originalFilters, apiTab },
    me: { role },
  } = state;

  // `apiTab` stores the position among VISIBLE tabs, not the ApiTabIndex
  // enum. On whitelabels that hide tabs (e.g. previews without Search/Social)
  // Simplifi's position (4) !== ApiTabIndex.Simplifi (6), so comparing raw
  // values wrongly took the foreign-tab branch ON the tab itself — the
  // cross-tab translation preserved advertisers but dropped the DSP-specific
  // campaign selection, so filtering by a campaign never reached the API
  // (flank #3890, Uros's report). Translate position → enum before comparing.
  const apiTabEnum = apiTabPositionToEnum(selectVisibleReportingScreenTabs(state), apiTab!);

  if (apiTabEnum !== foreignApiTab) {
    const reportingDashboardWhitelabels = selectReportingDashboardWhitelabels(state);
    const reportingDashboardClients = selectReportingDashboardClients(
      state,
    ) as unknown as IClientControlOption<IParsedAdvertiser>[];

    const storedFilters = !originalFilters.whitelabels.length
      ? {
          ...originalFilters,
          whitelabels: getWhitelabelOptions({
            userRole: role!,
            whitelabels: reportingDashboardWhitelabels,
          }) as IWhitelabelControlOption<IParsedAdvertiser>[],
        }
      : originalFilters;

    return getUpdatedTabFilters(
      foreignApiTab,
      apiTabEnum,
      storedFilters,
      reportingDashboardWhitelabels,
      reportingDashboardClients,
      role!,
    );
  }

  return originalFilters;
};
